/**
 * Simple object check.
 * Returns true if item is a plain object (not null, not array).
 *
 * @param item - The item to check
 * @returns true if item is a plain object
 */
export function isObject(item: unknown): item is Record<string, unknown> {
    return typeof item === "object" && item !== null && !Array.isArray(item);
}

/**
 * Deep merge two objects.
 * Properties from source take precedence over target.
 * Recursively merges nested objects.
 * Undefined values in source are skipped (won't override target values).
 *
 * @param target - The base object
 * @param source - The object to merge into target (takes precedence)
 * @returns Merged object with source values taking precedence
 *
 * @example
 * ```ts
 * const base = { a: 1, b: { c: 2 } };
 * const override = { b: { d: 3 } };
 * deepMerge(base, override);
 * // Returns: { a: 1, b: { c: 2, d: 3 } }
 * ```
 */
export function deepMerge<T extends Record<string, unknown>>(
    target: T,
    source: Partial<T> | undefined | null
): T {
    if (!source) {
        return target;
    }

    const output = { ...target };

    if (isObject(target) && isObject(source)) {
        for (const key of Object.keys(source)) {
            const sourceValue = source[key];
            const targetValue = target[key];

            // Skip undefined values in source
            if (sourceValue === undefined) {
                continue;
            }

            // If both values are objects, merge recursively
            if (isObject(sourceValue) && isObject(targetValue)) {
                (output as Record<string, unknown>)[key] = deepMerge(
                    targetValue as Record<string, unknown>,
                    sourceValue as Record<string, unknown>
                );
            } else {
                // Otherwise, source value takes precedence
                (output as Record<string, unknown>)[key] = sourceValue;
            }
        }
    }

    return output;
}
